import React from "react"
import { useParams } from "react-router-dom"

import { Button, FormControl, Grid, TextField, Typography } from "@material-ui/core"
import { Loading, Notification, Portlet } from "@/components"

import { TrackableLinkUTMMetricResponse } from "@/protocols/trackableLinks"
import { DEFAULT_WHERE_DATA, DefaultWhereDataType } from "@/utils/pagination"
import useValidation, { ErrorType } from "@/hooks/useValidation"

import ApiService from "@/services/Api"
import ErrorHandlerService from "@/services/ErrorHandler"

import useStyles from "@/pages/Admin/TrackableLinks/TrackableLinkMetrics/TrackableLinkInteractions/styles"
import TrackableLinkUTMMetricsSkeleton from "@/pages/Admin/TrackableLinks/TrackableLinkMetrics/TrackableLinkUTMMetrics/TrackableLinkUTMMetricsSkeleton"
import DataTableWithPagination from "@/components/DataTableWithPagination"
import { TableColumn } from "@/components/DataTable"
import { fullDatetime } from "@/utils/time"

export type UTMMetricsTableSearchFilters = {
	utmType: string | null
	utmValue: string | null
}

export type FormattedUTMMetricsDataResponse = {
	type: string
	value: string
	clicksCount: number
	sentMessagesCount: number
	createdAt: Date
}

type FormattedUTMMetricsData = {
	utmMetrics: FormattedUTMMetricsDataResponse[]
	count: number
}

const utmMetricsTableColumns: TableColumn<FormattedUTMMetricsDataResponse>[] = [
	{ field: "type", label: "Tipo de UTM" },
	{ field: "value", label: "Valor" },
	{ field: "clicksCount", label: "Quantidade de cliques" },
	{ field: "sentMessagesCount", label: "Quantidade de mensagens enviadas" },
	{ field: "createdAt", label: "Criado em", rowRender: (value) => fullDatetime(new Date(value)) }
]

const TrackableLinkUTMMetrics: React.FC = () => {
	const { trackableLinkId } = useParams<{ trackableLinkId?: string }>()
	const { validation, triggerValidation } = useValidation()

	const [trackableLinkUTMMetrics, setTrackableLinkUTMMetrics] = React.useState<FormattedUTMMetricsData>({
		utmMetrics: [],
		count: 0
	})
	const [loading, setLoading] = React.useState(false)
	const [paginationState, setPaginationState] = React.useState<DefaultWhereDataType>(DEFAULT_WHERE_DATA)
	const [utmListSearchFilters, setUtmListSearchFilters] = React.useState<UTMMetricsTableSearchFilters>({
		utmType: null,
		utmValue: null
	})

	const classes = useStyles()

	const handleGetTrackableLinkUTMMetrics = async () => {
		setLoading(true)
		try {
			const { data } = await ApiService.get<TrackableLinkUTMMetricResponse>(`/trackable-links/${trackableLinkId}/utm-metrics`, {
				params: {
					searchType: utmListSearchFilters.utmType,
					searchValue: utmListSearchFilters.utmValue,
					rowsPerPage: paginationState.rowsPerPage,
					page: paginationState.page
				}
			})

			setTrackableLinkUTMMetrics({
				utmMetrics: data.trackableLinkUTMMetrics,
				count: data.count
			})
		} catch (error) {
			Notification.error({ message: "Ocorreu um erro ao buscar as UTMs" })

			triggerValidation(error as ErrorType)
			ErrorHandlerService.handle(error as ErrorType)
		}

		setLoading(false)
	}

	const handleChangeEditFilters = (data: Partial<UTMMetricsTableSearchFilters>) => {
		setUtmListSearchFilters({
			...utmListSearchFilters,
			...data
		})
	}

	const handlePaginationState = (data: Partial<DefaultWhereDataType>) => {
		setPaginationState(lastState => ({
			...lastState,
			...data
		}))
	}

	const handleApplyFilters = async () => {
		handlePaginationState({ page: DEFAULT_WHERE_DATA.page })
		await handleGetTrackableLinkUTMMetrics()
	}

	React.useEffect(() => {
		handleGetTrackableLinkUTMMetrics()
	}, [paginationState])

	return (
		<Loading
			customLoadingElement={<TrackableLinkUTMMetricsSkeleton />}
			loading={loading}
		>
			<Grid container direction="column" spacing={3}>
				<Grid item>
					<Typography
						variant="h5"
						className={classes.titleText}
					>
						UTMs
					</Typography>
				</Grid>

				<Grid item>
					<Portlet>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<form onSubmit={handleApplyFilters}>
									<Grid container alignItems="center" spacing={1}>
										<Grid item xs={4}>
											<FormControl fullWidth>
												<TextField
													value={utmListSearchFilters.utmType}
													onChange={
														({ target }) => handleChangeEditFilters({ utmType: target.value?.trimStart() })
													}
													size="medium"
													placeholder="Busque por tipo de UTM"
													fullWidth
													variant="outlined"
													helperText={validation.searchType}
													error={!!validation.searchType}
												/>
											</FormControl>
										</Grid>
										<Grid item xs={4}>
											<FormControl fullWidth>
												<TextField
													value={utmListSearchFilters.utmValue}
													onChange={
														({ target }) => handleChangeEditFilters({ utmValue: target.value?.trimStart() })
													}
													size="medium"
													placeholder="Valor da UTM"
													fullWidth
													variant="outlined"
													helperText={validation.searchValue}
													error={!!validation.searchValue}
												/>
											</FormControl>
										</Grid>
										<Grid item>
											<Button
												variant="contained"
												color="primary"
												type="submit"
											>
												Buscar
											</Button>
										</Grid>
									</Grid>
								</form>
							</Grid>

							<Grid item xs={12}>
								<DataTableWithPagination
									columns={utmMetricsTableColumns}
									data={trackableLinkUTMMetrics.utmMetrics}
									page={paginationState.page}
									rowsPerPage={paginationState.rowsPerPage}
									totalRows={trackableLinkUTMMetrics.count}
									onPaginationChange={handlePaginationState}
								/>
							</Grid>
						</Grid>
					</Portlet>
				</Grid>
			</Grid>
		</Loading>
	)
}

export default TrackableLinkUTMMetrics
